// Wild goons — red-tinted antagonist cats that drift across the paddy.
//
// Spawn loop fires every WildGoonConfig.spawnIntervalMs, dropping a wild
// goon at a random edge tile if there's room (capped at maxAlive). Each one
// wanders normally and lives for lifespanMs; if it walks within
// conversionRadius of a Temple, it converts into a loyal Peasant Cat the
// player owns. Otherwise it despawns silently.

import * as THREE from 'three'
import { createSyntyChar, syntyCharsReady, scaleOf, playClip } from './syntyChars.js'
import { groundLift } from './paddyRows.js'
import { isBlocked } from './collision.js'
import { getCampfires } from './effects.js'
import gsap from 'gsap'
import { WildGoonConfig, BuildingTypes, GoonTypes, IdleConfig, RaidDamageConfig, PlotTypes, WorkModes, FeralConfig } from '../config/balance.js'
import { Events, emit, on } from '../ui/eventBus.js'
import * as season from '../game/seasonManager.js'
import { getBuildings } from '../game/buildingState.js'
import { getPlot, isHomeTile, tileToWorld } from '../game/plotState.js'
import { worldToAxial, inField, isInFieldWorld, key as hexKey, fieldRadiusWorld, GROUND_Y } from './hexGrid.js'

const FIELD_HALF = fieldRadiusWorld()
import { damageTile, isDamaged } from '../game/damageManager.js'
import { findPeasantsNear, injurePeasant, markRestless, extractExpiredRestless } from './peasants.js'
import { splatterBurst } from './effects.js'

let sceneRef = null
let heroRef = null
const wildGoons = new Set() // each entry: { mesh, spawnedAt }

// Spawn ticker — self-rescheduling so we can switch cadence on raid
// start/end without losing the next tick. Re-reads its interval each cycle.
let spawnTimer = null
let raiding = false
// A director raid (raidDirector.js) is a BOUNDED raid: exactly `size` goons arrive in one band from one edge, with no
// reinforcement waves. The idle raid has no spec and stays the endless horde.
let raidSpec = null
const horde = () => raiding && !raidSpec

function scheduleNextSpawn() {
  if (spawnTimer) clearTimeout(spawnTimer)
  const interval = horde() ? IdleConfig.raidSpawnIntervalMs : WildGoonConfig.spawnIntervalMs
  spawnTimer = setTimeout(() => {
    trySpawn()
    scheduleNextSpawn()
  }, interval)
}

// Feral ticker (Step E) — on a slow cadence, a neglected citizen may turn
// feral: the player loses a loyal cat and a hostile goon spawns in its place.
// Educated (School) / blessed (Temple) citizens are immune, so a well-tended
// village simply never produces a candidate and the roll is a no-op.
let feralTimer = null
function scheduleNextFeral() {
  if (feralTimer) clearTimeout(feralTimer)
  feralTimer = setTimeout(() => {
    rollFeral()
    scheduleNextFeral()
  }, FeralConfig.checkIntervalMs)
}

// The roll no longer snatches a citizen: it makes one RESTLESS (peasants.markRestless — a mark over its head and a warning), and
// tickRestless() below turns it feral only if, FeralConfig.restlessMs later, nobody has schooled or blessed it.
function rollFeral() {
  if (!sceneRef) return
  if (season.totalCitizens() < FeralConfig.minCitizens) return // don't gut a tiny village
  if (Math.random() >= FeralConfig.chancePerCheck) return
  markRestless() // null when the School/Temple shield covers everyone (or enough are restless already) — village holds
}
function tickRestless() {
  if (!sceneRef) return
  if (season.totalCitizens() < FeralConfig.minCitizens) return
  const pos = extractExpiredRestless()
  if (!pos) return
  spawnFeralAt(pos.x, pos.z)
  emit(Events.CitizenFeral, {})
  // Say why, so the player connects the loss to schooling: build a School (or bless citizens at the Temple).
  const schooled = getBuildings().some((b) => b.type === BuildingTypes.SCHOOL)
  emit(Events.NarrativeBeat, {
    title: 'A child turned feral! 🐈‍⬛',
    body: schooled
      ? `A restless cat went ${Math.round(FeralConfig.restlessMs / 1000)} seconds without schooling, seized a torch and ran wild. Send your citizens past the School to graduate them — the educated never turn.`
      : `A restless cat went ${Math.round(FeralConfig.restlessMs / 1000)} seconds without schooling, seized a torch and ran wild. Build a School to educate the young — educated citizens never turn feral.`,
  })
}

// Drop a hostile goon where a citizen just turned, popping it in out of the
// corruption burst before it starts roaming + torching like any wild goon.
function spawnFeralAt(x, z) {
  const goon = buildWildGoon()
  goon.position.set(x, GROUND_Y, z)
  goon.scale.setScalar(0.01)
  sceneRef.add(goon)
  const entry = { mesh: goon, spawnedAt: Date.now() }
  wildGoons.add(entry)
  const s = scaleOf(goon)
  gsap.to(goon.scale, {
    x: s, y: s, z: s,
    duration: 0.4,
    ease: 'back.out(1.7)',
    onComplete: () => { if (goon.parent === sceneRef) wander(goon) },
  })
}

export function createWildGoons({ scene, hero }) {
  if (import.meta.env?.DEV) window.__wild = { spawn: () => spawnOne(), count: () => wildGoons.size, noLag: () => gsap.ticker.lagSmoothing(0), kill: (i = 0) => shootKill([...wildGoons][i]), all: () => [...wildGoons].map((e) => e.mesh), freeze: () => [...wildGoons].forEach((e) => gsap.killTweensOf(e.mesh.position)) } // dev/test hook
  sceneRef = scene
  heroRef = hero ?? null
  scheduleNextSpawn()
  scheduleNextFeral()
  setInterval(tickRestless, 1000) // a restless citizen whose time is up turns
  on(Events.RaidStart, onRaidStart)
  on(Events.RaidEnd, onRaidEnd)
}

function onRaidStart(p) {
  raiding = true
  raidSpec = p?.size ? p : null
  // Goons already out on the field when the raid begins pick up the raid pace straight away (not after their current leg).
  for (const e of wildGoons) {
    if (e.fleeing || e.dying || !e.mesh || e.mesh.parent !== sceneRef) continue
    wander(e.mesh) // (wander drops the leg it was on)
  }
  if (raidSpec) { // a bounded raid: one band, from the announced edge
    const center = (Math.random() - 0.5) * (FIELD_HALF * 2 - 8)
    for (let i = 0; i < raidSpec.size; i += 1) spawnOne({ edge: raidSpec.edge, center, waveId: raidSpec.id })
    emit(Events.RaidWave, { count: raidSpec.size, alive: wildGoons.size })
    scheduleNextSpawn()
    return
  }
  trySpawn()          // opening wave — the horde hits the instant the raid begins
  scheduleNextSpawn() // then reinforcement waves on the faster raid cadence
}

function onRaidEnd() {
  raiding = false
  raidSpec = null
  scheduleNextSpawn() // back to slow trickle
  // Every alive wild goon scatters for the nearest map edge, then fades
  // out as they cross the boundary — "head for the hills".
  for (const entry of [...wildGoons]) scatterEntry(entry)
}

// A retreating goon keeps its place in `wildGoons` (so it can still be shot or touched) but is flagged `fleeing`:
// it walks to the nearest edge at a randomly chosen pace and is removed once it has left the field.
function pickFleeSpeed() {
  let r = Math.random()
  for (const t of WildGoonConfig.fleeTiers) { if (r < t.share) return t; r -= t.share }
  return WildGoonConfig.fleeTiers[WildGoonConfig.fleeTiers.length - 1]
}

function scatterEntry(entry) {
  if (!entry.mesh || entry.dying || entry.fleeing) return
  entry.fleeing = true
  const m = entry.mesh
  gsap.killTweensOf(m.position)
  gsap.killTweensOf(m.scale)
  gsap.killTweensOf(m.rotation)
  if (m.userData.synty && m.userData.anim !== 'walk') { m.userData.anim = 'walk'; playClip(m, 'torchwalk', { fade: 0.15 }) }

  // Pick the nearest of the four edges and walk PAST it by 1 tile so the goon visibly clears the boundary.
  const half = FIELD_HALF
  const { x, z } = m.position
  const distLeft = x + half, distRight = half - x
  const distTop = z + half, distBottom = half - z
  const min = Math.min(distLeft, distRight, distTop, distBottom)
  let targetX = x, targetZ = z
  if (min === distLeft) targetX = -half - 1
  else if (min === distRight) targetX = half + 1
  else if (min === distTop) targetZ = -half - 1
  else targetZ = half + 1

  const dx = targetX - x, dz = targetZ - z
  const dist = Math.hypot(dx, dz)
  const tier = pickFleeSpeed()
  entry.fleePace = tier.name; m.userData.fleePace = tier.name
  const want = Math.atan2(dx, dz)
  if (tier.name === 'fast') m.rotation.y = want // dashes off immediately
  else { // turns, then walks off
    const cur = m.rotation.y
    const diff = Math.atan2(Math.sin(want - cur), Math.cos(want - cur))
    gsap.to(m.rotation, { y: cur + diff, duration: 0.4, ease: 'power1.inOut' })
  }
  gsap.to(m.position, {
    x: targetX, z: targetZ, duration: Math.max(0.5, dist / tier.speed), ease: 'none',
    onComplete: () => { wildGoons.delete(entry); sceneRef?.remove(m) },
  })
}

// One spawn tick. During a raid this drops a whole WAVE (raidSpawnBatch goons)
// from random edges so the attack reads as an invasion, not a trickle; in
// normal play it's a single ambient wanderer. The cap is re-checked per goon
// so a wave never overshoots raidMaxAlive.
function trySpawn() {
  const cap = horde() ? IdleConfig.raidMaxAlive : WildGoonConfig.maxAlive
  const batch = horde() ? IdleConfig.raidSpawnBatch : 1
  let spawned = 0
  const alive = () => { let n = 0; for (const e of wildGoons) if (!e.fleeing) n++; return n } // retreating goons don't count against the cap
  for (let i = 0; i < batch; i += 1) {
    if (alive() >= cap) break
    spawnOne()
    spawned += 1
  }
  // Tell the rest of the game how many raiders REALLY arrived (the Capital trip ledger quotes this).
  if (horde() && spawned > 0) emit(Events.RaidWave, { count: spawned, alive: wildGoons.size })
}

// opts (director raids): { edge: 0–3, center, waveId } — a band that arrives together from one edge.
function spawnOne(opts = {}) {
  const goon = buildWildGoon()
  const half = FIELD_HALF + 1.2 // out on the surrounding land; they only become visible once they reach the tiles
  // Spawn on a random edge — feels like they "wandered in" from outside.
  const edge = opts.edge ?? Math.floor(Math.random() * 4)
  const along = opts.center != null ? opts.center + (Math.random() - 0.5) * 6 : (Math.random() - 0.5) * (FIELD_HALF * 2 - 1)
  if (edge === 0) goon.position.set(-half, GROUND_Y, along)
  else if (edge === 1) goon.position.set(half, GROUND_Y, along)
  else if (edge === 2) goon.position.set(along, GROUND_Y, -half)
  else goon.position.set(along, GROUND_Y, half)
  goon.scale.setScalar(scaleOf(goon))
  hideUntilOnTiles(goon)
  sceneRef.add(goon)
  const entry = { mesh: goon, spawnedAt: Date.now(), waveId: opts.waveId ?? null }
  wildGoons.add(entry)
  wander(goon)
}

// For the raid director: how many goons of this raid are still on the field (not fleeing)?
export function waveAlive(waveId) {
  let n = 0
  for (const e of wildGoons) if (e.waveId === waveId && !e.fleeing && !e.dying) n++
  return n
}
export function waveFleeing(waveId) {
  let n = 0
  for (const e of wildGoons) if (e.waveId === waveId && e.fleeing) n++
  return n
}

// Torch the goons carry (they're the arsonists who set fields + buildings
// ablaze). Shared geometry/material across all goons; the flame flickers via a
// cheap per-frame scale in tickWildGoons (no gsap → nothing to clean up when a
// goon is slain). Always lit — at night it reads as a moving point of light.
const TORCH_STICK_GEOM = new THREE.BoxGeometry(0.05, 0.5, 0.05)
const TORCH_STICK_MAT = new THREE.MeshLambertMaterial({ color: 0x4a3220 })
const TORCH_FLAME_GEOM = new THREE.ConeGeometry(0.1, 0.32, 6)
const TORCH_FLAME_OUTER_MAT = new THREE.MeshBasicMaterial({ color: 0xff6a1a, transparent: true, opacity: 0.95, depthWrite: false })
const TORCH_FLAME_INNER_MAT = new THREE.MeshBasicMaterial({ color: 0xffd84d, transparent: true, opacity: 0.95, depthWrite: false })
let _torchGlowMat = null
function torchGlowMat() {
  if (_torchGlowMat) return _torchGlowMat
  const cv = document.createElement('canvas')
  cv.width = cv.height = 64
  const ctx = cv.getContext('2d')
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32)
  g.addColorStop(0, 'rgba(255,232,176,0.9)')
  g.addColorStop(0.4, 'rgba(255,162,80,0.35)')
  g.addColorStop(1, 'rgba(255,140,60,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, 64, 64)
  const tex = new THREE.CanvasTexture(cv)
  tex.colorSpace = THREE.SRGBColorSpace
  _torchGlowMat = new THREE.SpriteMaterial({ map: tex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, opacity: 0.85 })
  return _torchGlowMat
}

// Build a torch held out at the goon's side; returns its flame group so the
// caller can flicker it. Coordinates are in unscaled cat-local space.
function buildTorch() {
  const torch = new THREE.Group()
  torch.position.set(0.36, 0.5, 0.16)
  torch.rotation.z = -0.42 // angled outward like it's gripped
  const stick = new THREE.Mesh(TORCH_STICK_GEOM, TORCH_STICK_MAT)
  stick.position.y = 0.22
  torch.add(stick)
  const flame = new THREE.Group()
  flame.position.y = 0.52
  const outer = new THREE.Mesh(TORCH_FLAME_GEOM, TORCH_FLAME_OUTER_MAT)
  const inner = new THREE.Mesh(TORCH_FLAME_GEOM, TORCH_FLAME_INNER_MAT)
  inner.scale.setScalar(0.55)
  inner.position.y = 0.05
  const glow = new THREE.Sprite(torchGlowMat())
  glow.scale.set(0.95, 0.95, 1)
  glow.position.y = 0.06
  flame.add(outer, inner, glow)
  torch.add(flame)
  return { torch, flame }
}

// Raiders start invisible out on the surrounding land and fade in the moment they reach the tiles (and fade
// out again if they wander off), so they never seem to come from the sea or from thin air.
function hideUntilOnTiles(goon) {
  goon.userData.onTiles = false
  goon.visible = false
  const mat = goon.userData.mat
  if (mat) { mat.transparent = true; mat.opacity = 0 }
}
function updateVisibility(goon) {
  const on = isInFieldWorld(goon.position.x, goon.position.z)
  if (on === goon.userData.onTiles) return
  goon.userData.onTiles = on
  const mat = goon.userData.mat
  gsap.killTweensOf(mat ?? {})
  if (on) {
    goon.visible = true
    if (mat) gsap.to(mat, { opacity: 1, duration: 0.45, ease: 'power1.out', onComplete: () => { mat.transparent = false } })
  } else if (mat) {
    mat.transparent = true
    gsap.to(mat, { opacity: 0, duration: 0.35, ease: 'power1.in', onComplete: () => { if (!goon.userData.onTiles) goon.visible = false } })
  } else goon.visible = false
}

// The torch rides in the raider's RIGHT HAND (a child of the hand bone), so it follows every animation.
// Offsets are in the hand bone's own local space (metres) — tuned against the Mixamo "Standing Torch" clips.
// Local space of the Hand_R bone, 1 unit = 1 cm (before k). The Mixamo torch clips hang the torch arm at the hip and grip the stick
// ACROSS the fist (perpendicular to the forearm), pointing forward and a little up — measured from the finger curl axis. So the stick
// is turned 230° about the hand's x axis (the forearm), and its base is placed so the measured fist centre (x −12.5, y 1.0, z −1.2)
// sits 12 cm up the stick. (Before, the stick lay along the forearm, hidden inside the arm, leaving only a floating flame.)
const GRIP_ANGLE = (230 * Math.PI) / 180
export const TORCH_IN_HAND = {
  pos: [-12.5, 1.0 - 12 * Math.cos(GRIP_ANGLE), -1.2 - 12 * Math.sin(GRIP_ANGLE)],
  rot: [GRIP_ANGLE, 0, 0],
  k: 100,
}
// A real torch for the Synty raiders: a thick wooden stick with a charred, glowing head. The stick is self-lit a little so
// it still reads at night, when the flame is the only light and a plain dark stick would vanish (leaving a floating spark).
const HAND_STICK_GEOM = new THREE.CylinderGeometry(0.032, 0.026, 0.46, 8)
const HAND_STICK_MAT = new THREE.MeshLambertMaterial({ color: 0x6b4423, emissive: 0x4a2a10 })
const HAND_HEAD_GEOM = new THREE.BoxGeometry(0.085, 0.1, 0.085)
const HAND_HEAD_MAT = new THREE.MeshLambertMaterial({ color: 0x241510, emissive: 0xff4d12, emissiveIntensity: 0.55 })
export function equipTorch(goon) {
  const { torch, flame } = buildTorch()
  const T = TORCH_IN_HAND
  torch.children[0].geometry = HAND_STICK_GEOM // swap the thin placeholder stick for the thick lit one
  torch.children[0].material = HAND_STICK_MAT
  torch.children[0].position.y = 0.23
  const head = new THREE.Mesh(HAND_HEAD_GEOM, HAND_HEAD_MAT)
  head.position.y = 0.46
  torch.add(head)
  flame.position.y = 0.55
  torch.position.set(...T.pos)
  torch.rotation.set(...T.rot)
  torch.scale.setScalar(T.k)
  flame.children.forEach((c) => {
    if (!c.isSprite) return
    c.scale.multiplyScalar(0.6) // the halo is big at this scale
    c.material = c.material.clone(); c.material.depthTest = false; c.renderOrder = 20 // the glow shows even when the arm or body is in front of the torch
  })
  const hand = goon.getObjectByName('Hand_R')
  ;(hand ?? goon).add(torch)
  // The torch clips grip with the RIGHT hand, which read as the wrong hand on screen — so flip the raider left-for-right (the
  // model inside its holder, so the holder's own scale and position are untouched). The grip, the arm pose and the walk cycle
  // all stay exactly as authored, now with the torch in the other hand. Torch and flame are children of the hand, so they follow.
  if (hand) { const model = goon.children.find((c) => c !== torch && !c.isSprite && c.isObject3D) ?? goon.children[0]; if (model) model.scale.x *= -1 }
  goon.userData.flame = flame
  goon.userData.torch = torch
  return torch
}

// ── Raider animation: torch walk → light torch (rice rows / campfire) → burn (buildings) ─────────────────
// Distances are in world units. At the default zoom 1 world unit ≈ 178 px.
const NEAR_ENTER = 0.28         // ≈ 50 px (tightened from 100 px: a raider has to be right next to a building / fire)
const NEAR_EXIT = 0.42          // ≈ 75 px: ~25 px of margin so it doesn't flicker at the edge
const ROW_EXIT = 0.14           // ≈ 25 px beyond a rice row
const ACT_SLOW = 0.35           // while it acts it drifts slowly, so the clip actually plays before it moves on
function nearStructure(x, z, r) {
  if (isBlocked(x, z)) return true
  for (const f of [0.4, 0.75, 1]) {
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2
      if (isBlocked(x + Math.cos(a) * r * f, z + Math.sin(a) * r * f)) return true
    }
  }
  return false
}
function nearFire(x, z, r) {
  for (const c of getCampfires()) if (c.obj.visible && Math.hypot(c.x - x, c.z - z) <= r) return true // campfires burn at night
  return false
}
function onRice(x, z, r) {
  if (groundLift(x, z) > 0.004) return true
  if (r <= 0) return false
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2
    if (groundLift(x + Math.cos(a) * r, z + Math.sin(a) * r) > 0.004) return true
  }
  return false
}
const ANIM_CLIP = { walk: 'torchwalk', light: 'torchlight', burn: 'torchburn' }
function setGoonAnim(goon, to) {
  const u = goon.userData
  if (u.anim === to) return
  u.anim = to
  playClip(goon, ANIM_CLIP[to], { fade: 0.25 })
  const tw = gsap.getTweensOf(goon.position)[0]
  if (tw) tw.timeScale(to === 'walk' ? 1 : ACT_SLOW)
}
function updateGoonAnim(goon) {
  if (!goon.visible) return
  const { x, z } = goon.position
  const u = goon.userData
  let want
  if (u.anim === 'burn') want = nearStructure(x, z, NEAR_EXIT) ? 'burn' : null
  else if (nearStructure(x, z, NEAR_ENTER)) want = 'burn'
  if (!want) {
    if (u.anim === 'light') want = (onRice(x, z, ROW_EXIT) || nearFire(x, z, NEAR_EXIT)) ? 'light' : 'walk'
    else want = (onRice(x, z, 0) || nearFire(x, z, NEAR_ENTER)) ? 'light' : 'walk'
  }
  setGoonAnim(goon, want)
}

// Raiders are Synty Warriors or Ninjas (random), each carrying the arson torch.
const RAIDER_TINT = new THREE.Color(1.0, 0.82, 0.8)
function buildSyntyRaider() {
  if (!syntyCharsReady()) return null
  const ninja = Math.random() < 0.5
  const goon = createSyntyChar(ninja ? 'ninja' : 'warrior', {
    palette: ninja ? 1 : 1 + Math.floor(Math.random() * 3), tint: RAIDER_TINT, emissive: 0x2a0600,
  })
  if (!goon) return null
  goon.userData.wild = true
  goon.userData.kind = ninja ? 'ninja' : 'warrior'
  equipTorch(goon)
  goon.userData.flamePhase = Math.random() * 6.28
  goon.userData.anim = 'walk'
  playClip(goon, 'torchwalk', { fade: 0 }) // holds the torch while walking
  return goon
}

function buildWildGoon() {
  const synty = buildSyntyRaider()
  if (synty) return synty
  const cat = new THREE.Group()
  cat.userData.wild = true

  const bodyMat = new THREE.MeshLambertMaterial({ color: WildGoonConfig.bodyColor })
  const headMat = new THREE.MeshLambertMaterial({ color: WildGoonConfig.headColor })
  const earMat = new THREE.MeshLambertMaterial({ color: WildGoonConfig.earColor })

  const body = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.6, 0.45), bodyMat)
  body.position.y = 0.32
  cat.add(body)

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.43, 0.43), headMat)
  head.position.y = 0.86
  cat.add(head)

  const earGeo = new THREE.BoxGeometry(0.14, 0.18, 0.14)
  const earL = new THREE.Mesh(earGeo, earMat)
  earL.position.set(-0.15, 1.16, 0)
  const earR = new THREE.Mesh(earGeo, earMat)
  earR.position.set(0.15, 1.16, 0)
  cat.add(earL, earR)

  // Brighter eyes than the loyal peasants — wild goons read as more aggressive.
  const eyeGeo = new THREE.BoxGeometry(0.07, 0.07, 0.05)
  const eyeMat = new THREE.MeshBasicMaterial({ color: WildGoonConfig.eyeColor })
  const eyeL = new THREE.Mesh(eyeGeo, eyeMat)
  eyeL.position.set(-0.1, 0.88, 0.24)
  const eyeR = new THREE.Mesh(eyeGeo, eyeMat)
  eyeR.position.set(0.1, 0.88, 0.24)
  cat.add(eyeL, eyeR)

  // A lit torch — the goon's arson tool. Flame ref stored for per-frame flicker.
  const { torch, flame } = buildTorch()
  cat.add(torch)
  cat.userData.flame = flame
  cat.userData.flamePhase = Math.random() * 6.28

  return cat
}

function wander(goon) {
  gsap.killTweensOf(goon.position) // never two walking legs at once (a raid start restarts a goon mid-leg)
  const range = FIELD_HALF * 2 - 1
  const x = (Math.random() - 0.5) * range
  const z = (Math.random() - 0.5) * range
  const dx = x - goon.position.x
  const dz = z - goon.position.z
  const dist = Math.hypot(dx, dz)
  const pace = raiding ? (WildGoonConfig.raidSpeedMult ?? 1) : 1 // a raid is a rush: the whole band moves faster
  const duration = Math.max(1.5, dist * 1.4) / pace
  goon.rotation.y = Math.atan2(dx, dz)
  const tw = gsap.to(goon.position, {
    x,
    z,
    duration,
    ease: 'none',
    onComplete: () => {
      // Only resume wandering if the goon is still alive.
      if (goon.parent === sceneRef) wander(goon)
    },
  })
  if (goon.userData.anim && goon.userData.anim !== 'walk') tw.timeScale(ACT_SLOW) // still acting: keep drifting slowly
}

// Per-frame check: wild goons can (a) be converted by Temple proximity,
// (b) kill loyal peasants they bump into, or (c) eventually time out and leave.
// Order: kill first (the goon strikes on its way through), then check Temple
// (a Temple still catches the goon afterward). Lifespan only matters if the
// goon survives both.
export function tickWildGoons() {
  if (wildGoons.size === 0) return
  const now = Date.now()
  const templePositions = getTemplePositions()
  const ft = now * 0.001 // flame flicker clock (seconds)
  for (const entry of [...wildGoons]) {
    const { mesh, spawnedAt } = entry

    updateVisibility(mesh) // fade in / out as it crosses onto / off the tiles
    if (mesh.userData.synty && !entry.dying && !entry.fleeing) updateGoonAnim(mesh)
    if (mesh.userData.synty && !entry.dying) mesh.position.y += (GROUND_Y + groundLift(mesh.position.x, mesh.position.z) - mesh.position.y) * 0.35 // over the rice rows

    // Flicker the carried torch flame (cheap scale wobble; no tweens to leak).
    const flame = mesh.userData.flame
    if (flame) {
      const ph = mesh.userData.flamePhase
      flame.scale.set(
        0.9 + 0.12 * Math.sin(ft * 7 + ph),
        0.82 + 0.24 * Math.sin(ft * 9 + ph) + 0.06 * Math.sin(ft * 23 + ph),
        0.9 + 0.12 * Math.cos(ft * 8 + ph),
      )
    }

    // (a) Hero attacks first — the player walking into a wild goon slays it
    // outright. Priority over collateral kills + Temple capture so the player
    // feels powerful taking direct action.
    if (heroRef) {
      const dx = mesh.position.x - heroRef.position.x
      const dz = mesh.position.z - heroRef.position.z
      if (Math.hypot(dx, dz) <= WildGoonConfig.heroKillRadius) {
        slay(entry)
        continue
      }
    }

    // A retreating goon only leaves: no brawls, arson, temple or lifespan checks (it can still be shot or touched above).
    if (entry.fleeing) continue

    // (b) Brawl with loyal citizens in range — the citizens overwhelm and
    // DESTROY the goon, but everyone it fought drops to injured (skinny) and
    // earns nothing until healed. The goon is gone for this frame → continue.
    const victims = findPeasantsNear(mesh.position, WildGoonConfig.killRadius)
    if (victims.length) {
      for (const v of victims) injurePeasant(v)
      splatterBurst({
        scene: sceneRef,
        position: mesh.position,
        colors: [WildGoonConfig.headColor, WildGoonConfig.bodyColor, WildGoonConfig.eyeColor],
      })
      sceneRef.remove(mesh)
      wildGoons.delete(entry)
      emit(Events.WildGoonBrawled, { waveId: entry.waveId })
      continue
    }

    // (b.5) RAID arson — during an active raid the goon may also torch the
    // building / farm tile it's standing on. Tuned to ~8%/sec via Poisson:
    // (1 - (1 - p)^dt). dt is 1 (we tick once per frame ~60fps but only
    // sample arson every ~1s by checking against now - lastArsonAt).
    if (raiding && (!entry.lastArsonAt || now - entry.lastArsonAt > 1000)) {
      entry.lastArsonAt = now
      if (Math.random() < RaidDamageConfig.damageChancePerSec) tryArson(mesh.position)
    }

    // (c) Temple conversion.
    let converted = false
    for (const tp of templePositions) {
      const dx = mesh.position.x - tp.x
      const dz = mesh.position.z - tp.z
      if (Math.hypot(dx, dz) <= WildGoonConfig.conversionRadius) {
        convert(entry)
        converted = true
        break
      }
    }
    if (converted) continue

    // (d) Lifespan expired → quiet despawn.
    if (!entry.waveId && now - spawnedAt > WildGoonConfig.lifespanMs) despawn(entry) // director raiders stay until the raid ends
  }
}

// Convert a world (x, z) back to the hex tile key under it (or null if off
// the field).
function worldToTileIndex(wx, wz) {
  const { q, r } = worldToAxial(wx, wz)
  if (!inField(q, r)) return null
  return hexKey(q, r)
}

// During raid: pick the tile under the goon. If it carries a building or
// is a FARM plot, flag it as damaged (one-shot per tile). HOME tiles are
// spared so the player's spawn is sacred.
function tryArson(position) {
  const idx = worldToTileIndex(position.x, position.z)
  if (idx == null || isDamaged(idx) || isHomeTile(idx)) return
  // Workers protect wherever they're deployed: VILLAGE mode guards the
  // BUILDINGS (so they can't be torched) but leaves the paddies exposed;
  // FIELDS mode guards the PADDIES but leaves the village exposed.
  const village = season.getWorkMode() === WorkModes.VILLAGE
  const hasBuilding = getBuildings().some((b) => b.tileIndex === idx)
  if (hasBuilding) {
    if (village) return // guarded — the goon is driven off
    damageTile(idx, 'building')
    return
  }
  // FARM plot — scorched only if the workers are away in the village.
  const plot = getPlot(idx)
  if (plot && plot.type === PlotTypes.FARM) {
    if (!village) return // workers in the fields beat out the flames
    damageTile(idx, 'plot')
  }
}

function getTemplePositions() {
  return getBuildings()
    .filter((b) => b.type === BuildingTypes.TEMPLE)
    .map((b) => tileToWorld(b.tileIndex))
}

function convert(entry) {
  gsap.killTweensOf(entry.mesh.position)
  // Golden "sanctify" sparkle so the Temple's blessing reads as a reward.
  splatterBurst({
    scene: sceneRef,
    position: entry.mesh.position,
    colors: [0xffd84d, 0xfff3c4, 0xffb733],
    count: 14,
  })
  // Pop-out animation — feels like the Temple sanctifies the goon out of existence,
  // and the player picks up a new loyal peasant.
  gsap.to(entry.mesh.scale, {
    x: 0,
    y: 0,
    z: 0,
    duration: 0.35,
    ease: 'back.in(1.5)',
    onComplete: () => {
      sceneRef.remove(entry.mesh)
    },
  })
  wildGoons.delete(entry)
  season.adjustGoonsOwned(GoonTypes.PEASANT_CAT, 1)
  emit(Events.WildGoonConverted, { waveId: entry.waveId })
}

// Player-initiated kill — every cat death (touch-kill, gun shot, friendly
// fire, starvation) uses the same particle splatter cue so the player
// reads "this thing died" instantly. Routes through splatterBurst.
function slay(entry) {
  if (entry.dying) return
  entry.dying = true
  gsap.killTweensOf(entry.mesh.position)
  gsap.killTweensOf(entry.mesh.scale)
  gsap.killTweensOf(entry.mesh.rotation)
  splatterBurst({
    scene: sceneRef,
    position: entry.mesh.position,
    colors: [WildGoonConfig.headColor, WildGoonConfig.bodyColor, WildGoonConfig.eyeColor],
  })
  sceneRef.remove(entry.mesh)
  wildGoons.delete(entry)
  emit(Events.WildGoonSlain, { waveId: entry.waveId })
}

function despawn(entry) {
  gsap.killTweensOf(entry.mesh.position)
  gsap.to(entry.mesh.scale, {
    x: 0,
    y: 0,
    z: 0,
    duration: 0.5,
    ease: 'power1.in',
    onComplete: () => sceneRef.remove(entry.mesh),
  })
  wildGoons.delete(entry)
}

export function clearWildGoons() {
  for (const entry of [...wildGoons]) despawn(entry)
}

// ── GUN MODE ────────────────────────────────────────────────────────────
// Cast a ray from the camera through the pointer NDC into the scene; if it
// hits the mesh of a live wild goon, splatter it into low-poly shards and
// return true. heroController owns the gun-mode UX and calls this.
const _shootRaycaster = new THREE.Raycaster()

export function tryShoot(camera, pointerNdc) {
  if (!sceneRef || wildGoons.size === 0) return false
  _shootRaycaster.setFromCamera(pointerNdc, camera)
  // Hit-test against every visible mesh inside every live wild goon group.
  const meshes = []
  const meshToEntry = new Map()
  for (const entry of wildGoons) {
    if (!entry.mesh || entry.dying) continue
    entry.mesh.traverse((obj) => {
      if (obj.isMesh) {
        meshes.push(obj)
        meshToEntry.set(obj, entry)
      }
    })
  }
  if (meshes.length === 0) return false
  const hits = _shootRaycaster.intersectObjects(meshes, false)
  if (hits.length === 0) return false
  const entry = meshToEntry.get(hits[0].object)
  if (!entry) return false
  splatter(entry)
  return true
}

// Gun-shot kill — same splatter cue as the touch-kill `slay`. Kept as a
// separate entry point so future shot-specific tweaks (sound, screen
// shake) can hook here without disturbing `slay`.
function splatter(entry) {
  if (entry.mesh?.userData?.synty) shootKill(entry)
  else slay(entry)
}

// A cross-hair kill: the raider plays its Mixamo "Falling Back Death", then fades out once the clip ends.
function shootKill(entry) {
  if (entry.dying) return
  const m = entry.mesh
  entry.dying = true
  gsap.killTweensOf(m.position)
  gsap.killTweensOf(m.rotation)
  wildGoons.delete(entry)
  emit(Events.WildGoonSlain, { waveId: entry.waveId }) // the kill counts (and pays) the moment it is shot
  const mat = m.userData.mat
  const fadeOut = () => {
    if (m.userData.torch) m.userData.torch.visible = false
    if (!mat) { sceneRef?.remove(m); return }
    mat.transparent = true
    gsap.to(mat, { opacity: 0, duration: 0.7, ease: 'power1.in', onComplete: () => sceneRef?.remove(m) })
  }
  if (!playClip(m, 'death', { once: true, fade: 0.12, onDone: fadeOut })) { splatterBurst({ scene: sceneRef, position: m.position, colors: [WildGoonConfig.headColor, WildGoonConfig.bodyColor, WildGoonConfig.eyeColor] }); sceneRef.remove(m) }
}
